import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { carpoolsAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import StepWizard from '../components/StepWizard';
import { ActivityIcon, ActivityIconBadge } from '../components/appPage';
import { getActivityLabel } from '../utils/carpoolUi';
import './CreateCarpool.css';

// Activity types (matching backend config)
const ACTIVITY_TYPES = [
  { value: 'school' },
  { value: 'sport' },
  { value: 'music' },
  { value: 'art' },
  { value: 'dance' },
  { value: 'other' },
];

const CreateCarpool = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    activity: '',
    description: '',
    startAddress: '',
    endAddress: '',
    days: [],
    time: '16:00',
    startLocation: null,
    endLocation: null,
    maxMembers: 10,
    childSeatsPerCar: 3,
    contactPhone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usageStats, setUsageStats] = useState(null);

  const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  useEffect(() => {
    // Fetch usage stats to show limits
    const fetchUsage = async () => {
      try {
        const response = await carpoolsAPI.getUsageStats();
        setUsageStats(response.data);
      } catch (err) {
        console.error('Failed to fetch usage stats:', err);
      }
    };
    fetchUsage();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleActivitySelect = (activityValue) => {
    setFormData((prev) => ({
      ...prev,
      activity: activityValue,
    }));
  };

  const handleDayToggle = (day) => {
    const newDays = formData.days.includes(day)
      ? formData.days.filter((d) => d !== day)
      : [...formData.days, day];

    setFormData({ ...formData, days: newDays });
  };

  const handleLocationChange = (type, coords) => {
    setFormData((prev) => ({
      ...prev,
      [`${type}Location`]: coords,
    }));
  };

  const hasLocationSupport = useMemo(() => {
    return Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
  }, []);

  const requiresContactPhone = !user?.phone || String(user.phone).trim() === '';
  const isContactPhoneValid =
    !requiresContactPhone ||
    /^\+?[0-9\s()-]{6,20}$/.test((formData.contactPhone || '').trim());

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      if (requiresContactPhone && !isContactPhoneValid) {
        setError(t('carpools.contactPhoneRequired'));
        setLoading(false);
        return;
      }

      const carpoolData = {
        name: formData.name,
        activity: formData.activity,
        description: formData.description,
        address: formData.startAddress,
        startAddress: formData.startAddress,
        endAddress: formData.endAddress || formData.startAddress,
        startLatitude: formData.startLocation?.lat,
        startLongitude: formData.startLocation?.lng,
        endLatitude: formData.endLocation?.lat,
        endLongitude: formData.endLocation?.lng,
        schedule: {
          days: formData.days,
          time: formData.time,
        },
        maxMembers: formData.maxMembers,
        childSeatsPerCar: parseInt(formData.childSeatsPerCar),
        contactPhone: requiresContactPhone ? formData.contactPhone.trim() : undefined,
      };

      const response = await carpoolsAPI.createCarpool(carpoolData);
      navigate(`/carpools/${response.data.carpool.id}`);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.errors?.[0]?.msg || t('errors.generic'));
      setLoading(false);
    }
  };

  // Validation per step
  const isStep1Valid = formData.name.length >= 3 && formData.activity;
  const isStep2Valid = formData.startAddress.length >= 5;
  const isStep3Valid = formData.days.length > 0 && formData.time && isContactPhoneValid;

  const steps = [
    {
      label: 'Информация',
      title: 'Основна информация',
      subtitle: 'Дайте име на карпула и изберете типа активност',
      isInvalid: !isStep1Valid,
    },
    {
      label: 'Локация',
      title: 'Маршрут',
      subtitle: 'Въведете начална и крайна точка на карпула',
      isInvalid: !isStep2Valid,
    },
    {
      label: 'График',
      title: 'График',
      subtitle: 'Изберете дните и часа на карпула',
      isInvalid: !isStep3Valid,
    },
  ];

  // Check if user has reached limit
  const hasReachedLimit = usageStats && usageStats.usage?.carpoolsCreated?.remaining === 0;

  if (hasReachedLimit) {
    return (
      <div className="create-carpool-page page-shell">
        <header className="page-head">
          <h1 className="page-title">{t('carpools.create')}</h1>
        </header>
        <div className="create-carpool-card limit-reached">
          <div className="limit-icon">🚫</div>
          <h2>Достигнахте лимита</h2>
          <p>
            Като безплатен потребител можете да създадете максимум{' '}
            {usageStats.usage.carpoolsCreated.limit} карпул(а).
          </p>
          <p>Upgrade към Premium за неограничен брой карпули!</p>
          <div className="limit-actions">
            <button className="btn-secondary" onClick={() => navigate('/carpools')}>
              Към моите карпули
            </button>
            <button className="btn-primary">Upgrade to Premium</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-carpool-page page-shell">
      <header className="page-head">
        <div className="page-head__row">
          <div className="page-head__text">
            <h1 className="page-title">{t('carpools.create')}</h1>
            <p className="page-lead">
              {t(
                'carpools.createLead',
                'Стъпка по стъпка: информация, маршрут и график за вашия карпул.'
              )}
            </p>
          </div>
          {usageStats && !usageStats.isPremium && (
            <div className="usage-badge">
              {usageStats.usage.carpoolsCreated.current}/{usageStats.usage.carpoolsCreated.limit} карпули
            </div>
          )}
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <StepWizard
        steps={steps}
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        onComplete={handleSubmit}
        onCancel={() => navigate('/carpools')}
        isSubmitting={loading}
        submitLabel="Създай карпул"
        cancelLabel="Отказ"
        nextLabel="Напред"
        prevLabel="Назад"
      >
        {/* Step 1: Basic Info */}
        {currentStep === 0 && (
          <>
            <div className="form-group">
              <label htmlFor="name">{t('carpools.name')} *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Напр. Футбол петък"
                maxLength={100}
              />
              {formData.name.length > 0 && formData.name.length < 3 && (
                <span className="field-error">Името трябва да е поне 3 символа</span>
              )}
            </div>

            <div className="form-group">
              <label>Тип активност *</label>
              <div className="activity-grid">
                {ACTIVITY_TYPES.map((activity) => (
                  <button
                    key={activity.value}
                    type="button"
                    className={`activity-card ${formData.activity === activity.value ? 'selected' : ''}`}
                    onClick={() => handleActivitySelect(activity.value)}
                  >
                    <span className="activity-icon">
                      <ActivityIcon activity={activity.value} size={28} strokeWidth={2} />
                    </span>
                    <span className="activity-label">{t(`carpools.activityTypes.${activity.value}`)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Описание (по избор)</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Добавете допълнителна информация за карпула..."
                rows={3}
                maxLength={500}
              />
            </div>
          </>
        )}

        {/* Step 2: Location */}
        {currentStep === 1 && (
          <div className="route-grid">
            <div className="route-card">
              <h3>{t('carpools.startPoint')}</h3>
              <div className="form-group">
                <label>{t('carpools.startAddress')} *</label>
                {hasLocationSupport ? (
                  <div className="selected-address">
                    {formData.startAddress ? formData.startAddress : t('carpools.mapAddressPlaceholder')}
                  </div>
                ) : (
                  <input
                    type="text"
                    id="startAddress"
                    name="startAddress"
                    value={formData.startAddress}
                    onChange={handleChange}
                    placeholder={t('carpools.startAddressPlaceholder')}
                  />
                )}
              </div>
              <MapPicker
                label={t('carpools.startLocationOnMap')}
                value={formData.startLocation}
                onChange={(coords) => handleLocationChange('start', coords)}
                addressValue={formData.startAddress}
                onAddressChange={(address) => setFormData((prev) => ({ ...prev, startAddress: address }))}
                helperText={hasLocationSupport ? t('carpools.mapClickHelp') : t('carpools.mapApiKeyMissing')}
                disabled={!hasLocationSupport}
              />
            </div>

            <div className="route-card">
              <h3>{t('carpools.endPoint')}</h3>
              <div className="form-group">
                <label>{t('carpools.endAddress')}</label>
                {hasLocationSupport ? (
                  <div className="selected-address">
                    {formData.endAddress ? formData.endAddress : t('carpools.mapAddressPlaceholder')}
                  </div>
                ) : (
                  <input
                    type="text"
                    id="endAddress"
                    name="endAddress"
                    value={formData.endAddress}
                    onChange={handleChange}
                    placeholder={t('carpools.endAddressPlaceholder')}
                  />
                )}
              </div>
              <MapPicker
                label={t('carpools.endLocationOnMap')}
                value={formData.endLocation}
                onChange={(coords) => handleLocationChange('end', coords)}
                addressValue={formData.endAddress}
                onAddressChange={(address) => setFormData((prev) => ({ ...prev, endAddress: address }))}
                helperText={hasLocationSupport ? t('carpools.mapClickHelp') : t('carpools.mapApiKeyMissing')}
                disabled={!hasLocationSupport}
              />
            </div>

            <div className="form-group max-members-group">
              <label htmlFor="maxMembers">Максимален брой членове</label>
              <div className="slider-container">
                <input
                  type="range"
                  id="maxMembers"
                  name="maxMembers"
                  min="2"
                  max={usageStats?.isPremium ? 15 : 5}
                  value={formData.maxMembers}
                  onChange={handleChange}
                  className="slider"
                />
                <span className="slider-value">{formData.maxMembers}</span>
              </div>
              {!usageStats?.isPremium && (
                <span className="field-hint">Free план: до 5 члена. Upgrade за до 15.</span>
              )}
            </div>

            <div className="form-group child-seats-group">
              <label htmlFor="childSeatsPerCar">Брой места за деца на кола</label>
              <div className="seats-selector">
                {[1, 2, 3, 4].map((num) => (
                  <button
                    key={num}
                    type="button"
                    className={`seat-button ${parseInt(formData.childSeatsPerCar) === num ? 'selected' : ''}`}
                    onClick={() => setFormData((prev) => ({ ...prev, childSeatsPerCar: num }))}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <span className="field-hint">Колко деца може да превозва всяка кола (препоръчително: 3)</span>
            </div>
          </div>
        )}

        {/* Step 3: Schedule */}
        {currentStep === 2 && (
          <>
            <div className="form-group">
              <label>{t('carpools.selectDays')} *</label>
              <div className="days-selector">
                {weekDays.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`day-button ${formData.days.includes(day) ? 'selected' : ''}`}
                    onClick={() => handleDayToggle(day)}
                  >
                    {t(`carpools.${day}`)}
                  </button>
                ))}
              </div>
              {formData.days.length === 0 && (
                <span className="field-error">Изберете поне един ден</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="time">{t('carpools.selectTime')} *</label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
              />
            </div>

            {requiresContactPhone && (
              <div className="form-group">
                <label htmlFor="contactPhone">{t('carpools.contactPhoneLabel')} *</label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  placeholder={t('carpools.contactPhonePlaceholder')}
                />
                <span className="field-hint">{t('carpools.contactPhoneHint')}</span>
                {formData.contactPhone && !isContactPhoneValid && (
                  <span className="field-error">{t('carpools.contactPhoneInvalid')}</span>
                )}
              </div>
            )}

            <div className="review-section">
              <h3>Преглед</h3>
              <div className="review-grid">
                <div className="review-item">
                  <span className="review-label">Име:</span>
                  <span className="review-value">{formData.name}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Активност:</span>
                  <span className="review-value review-value--activity">
                    <ActivityIconBadge activity={formData.activity} size={22} />
                    {getActivityLabel(formData.activity, t)}
                  </span>
                </div>
                <div className="review-item">
                  <span className="review-label">Начало:</span>
                  <span className="review-value">{formData.startAddress}</span>
                </div>
                {formData.endAddress && (
                  <div className="review-item">
                    <span className="review-label">Край:</span>
                    <span className="review-value">{formData.endAddress}</span>
                  </div>
                )}
                <div className="review-item">
                  <span className="review-label">Дни:</span>
                  <span className="review-value">
                    {formData.days.map((d) => t(`carpools.${d}`)).join(', ')}
                  </span>
                </div>
                <div className="review-item">
                  <span className="review-label">Час:</span>
                  <span className="review-value">{formData.time}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Макс. членове:</span>
                  <span className="review-value">{formData.maxMembers}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Места за деца/кола:</span>
                  <span className="review-value">{formData.childSeatsPerCar}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </StepWizard>
    </div>
  );
};

export default CreateCarpool;
